import { ITmHomePlanTemplateCSProps } from '@/components/templates/tm-home-plan-detail/tm-home-plan-detail.types';
import { getEntriesWithFilter } from '@/lib/contentstack-sdk';
import { IHomePlansForGridResponse, IHousePlanHit } from '@/lib/search/types';
import { cache } from 'react';

interface GetPlansResult {
  enrichedPlans: IHousePlanHit[];
  fallbackFloorPlanImages: any | null;
  hasLocationConflict: boolean;
}

interface GetPlansArgs {
  planId: number;
  studioId: number | null;
  locationId: number | null;
}

export const getPlans = cache(
  async ({
    planId,
    studioId,
    locationId,
  }: GetPlansArgs): Promise<GetPlansResult> => {
    let fallbackFloorPlanImages: any | null = null;
    let hasLocationConflict = false;

    try {
      const query = new URLSearchParams({
        home_plan_id: String(planId),
        ...(studioId && { location_id: String(studioId) }),
      });

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/algolia/home-plans?${query.toString()}`,
        {
          next: { revalidate: 3600 },
        },
      );

      const data = (await res.json()) as IHousePlanHit[];

      // Fallback if needed
      if (data.length === 0 && locationId === null) {
        const fallbackQuery = new URLSearchParams({
          home_plan_id: String(planId),
          location_id: '1',
        });

        const fallbackRes = await fetch(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/algolia/home-plans?${fallbackQuery.toString()}`,
          {
            next: { revalidate: 3600 },
          },
        );

        const fallbackData = (await fallbackRes.json()) as IHousePlanHit[];

        fallbackFloorPlanImages =
          fallbackData.find((plan) => plan?.IsBaseElevation)?.FloorPlans?.[0]
            ?.FloorPlanImages ?? null;
      }

      if (data.length === 0 && studioId !== null) {
        hasLocationConflict = true;
      }

      // Fetch and cache CMS data
      const csRes = await getEntriesWithFilter({
        contentTypeUid: 'home_plan_detail',
        filters: [],
      });

      const csEntries = csRes as ITmHomePlanTemplateCSProps[];

      const contentstackData = csEntries.find(
        (entry) => Number(entry.home_plan_id?.select) === planId,
      );

      // Enrich with CMS data
      const enrichedPlans = data.map((plan) => ({
        ...plan,
        supports3D: contentstackData?.has_three_d,
      }));

      return {
        enrichedPlans,
        fallbackFloorPlanImages,
        hasLocationConflict,
      };
    } catch (error) {
      console.error('[getPlans] Error:', error);
      return {
        enrichedPlans: [],
        fallbackFloorPlanImages: null,
        hasLocationConflict: false,
      };
    }
  },
);

export const getAllPlans = cache(
  async (
    buildStudioId: string = '10',
  ): Promise<IHomePlansForGridResponse[]> => {
    try {
      const query = new URLSearchParams({
        design_studio_id: buildStudioId,
        is_for_grid: 'true',
      });

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/algolia/home-plans?${query.toString()}`,
        {
          next: { revalidate: 3600 },
        },
      );

      if (!res.ok) {
        console.error('Failed to fetch plans:', res.statusText);
        return [];
      }

      return (await res.json()) as IHomePlansForGridResponse[];
    } catch (err) {
      console.error('getAllPlans error:', err);
      return [];
    }
  },
);
